// Franchise territory availability. Single source of truth for the /franchise-opportunities
// map + grid, the application form's state dropdown, and any future admin filters.
//
// Excluded states (owned/reserved, not for sale) per Victor:
//   New England: CT, MA, RI, NH, VT, ME
//   Others: CO, NC, SC, MI

export type FranchiseStatus = "available" | "reserved" | "in_talks" | "taken";

export type StateTier = "starter" | "growth" | "flagship";

export interface FranchiseState {
  code: string;
  name: string;
  status: FranchiseStatus;
  /** Rough market-size bucket driving the starting territory fee range. */
  tier: StateTier;
  /** Optional short note shown on cards (e.g. "In talks — Washington"). */
  note?: string;
}

// Tier → starting-territory-fee range (public, kept as a range per plan).
export const TIER_RANGE: Record<StateTier, { label: string; min: number; max: number }> = {
  starter: { label: "Starting at $5,000", min: 5000, max: 6500 },
  growth: { label: "Starting at $6,500", min: 6500, max: 8000 },
  flagship: { label: "Starting at $8,000", min: 8000, max: 9500 },
};

// All 50 states + DC. Excluded set is reserved (not for sale). WA is flagged in_talks
// because Victor has warm leads there — still shown as available on the map but with a
// small "in talks" badge on the grid card.
export const FRANCHISE_STATES: FranchiseState[] = [
  { code: "AL", name: "Alabama", status: "available", tier: "growth" },
  { code: "AK", name: "Alaska", status: "available", tier: "starter" },
  { code: "AZ", name: "Arizona", status: "available", tier: "flagship" },
  { code: "AR", name: "Arkansas", status: "available", tier: "starter" },
  { code: "CA", name: "California", status: "available", tier: "flagship" },
  { code: "CO", name: "Colorado", status: "reserved", tier: "growth", note: "Reserved" },
  { code: "CT", name: "Connecticut", status: "reserved", tier: "growth", note: "Home market" },
  { code: "DE", name: "Delaware", status: "available", tier: "starter" },
  { code: "FL", name: "Florida", status: "available", tier: "flagship" },
  { code: "GA", name: "Georgia", status: "available", tier: "flagship" },
  { code: "HI", name: "Hawaii", status: "available", tier: "starter" },
  { code: "ID", name: "Idaho", status: "available", tier: "starter" },
  { code: "IL", name: "Illinois", status: "available", tier: "flagship" },
  { code: "IN", name: "Indiana", status: "available", tier: "growth" },
  { code: "IA", name: "Iowa", status: "available", tier: "starter" },
  { code: "KS", name: "Kansas", status: "available", tier: "starter" },
  { code: "KY", name: "Kentucky", status: "available", tier: "growth" },
  { code: "LA", name: "Louisiana", status: "available", tier: "growth" },
  { code: "ME", name: "Maine", status: "reserved", tier: "starter", note: "Home region" },
  { code: "MD", name: "Maryland", status: "available", tier: "growth" },
  { code: "MA", name: "Massachusetts", status: "reserved", tier: "growth", note: "Home market" },
  { code: "MI", name: "Michigan", status: "reserved", tier: "growth", note: "Reserved" },
  { code: "MN", name: "Minnesota", status: "available", tier: "growth" },
  { code: "MS", name: "Mississippi", status: "available", tier: "starter" },
  { code: "MO", name: "Missouri", status: "available", tier: "growth" },
  { code: "MT", name: "Montana", status: "available", tier: "starter" },
  { code: "NE", name: "Nebraska", status: "available", tier: "starter" },
  { code: "NV", name: "Nevada", status: "available", tier: "growth" },
  { code: "NH", name: "New Hampshire", status: "reserved", tier: "starter", note: "Home region" },
  { code: "NJ", name: "New Jersey", status: "available", tier: "flagship" },
  { code: "NM", name: "New Mexico", status: "available", tier: "starter" },
  { code: "NY", name: "New York", status: "available", tier: "flagship" },
  { code: "NC", name: "North Carolina", status: "reserved", tier: "growth", note: "Reserved" },
  { code: "ND", name: "North Dakota", status: "available", tier: "starter" },
  { code: "OH", name: "Ohio", status: "available", tier: "growth" },
  { code: "OK", name: "Oklahoma", status: "available", tier: "starter" },
  { code: "OR", name: "Oregon", status: "available", tier: "growth" },
  { code: "PA", name: "Pennsylvania", status: "available", tier: "flagship" },
  { code: "RI", name: "Rhode Island", status: "reserved", tier: "starter", note: "Home region" },
  { code: "SC", name: "South Carolina", status: "reserved", tier: "growth", note: "Reserved" },
  { code: "SD", name: "South Dakota", status: "available", tier: "starter" },
  { code: "TN", name: "Tennessee", status: "available", tier: "growth" },
  { code: "TX", name: "Texas", status: "available", tier: "flagship" },
  { code: "UT", name: "Utah", status: "available", tier: "growth" },
  { code: "VT", name: "Vermont", status: "reserved", tier: "starter", note: "Home region" },
  { code: "VA", name: "Virginia", status: "available", tier: "growth" },
  { code: "WA", name: "Washington", status: "in_talks", tier: "growth", note: "In talks — act fast" },
  { code: "WV", name: "West Virginia", status: "available", tier: "starter" },
  { code: "WI", name: "Wisconsin", status: "available", tier: "growth" },
  { code: "WY", name: "Wyoming", status: "available", tier: "starter" },
  { code: "DC", name: "District of Columbia", status: "available", tier: "starter" },
];

export const STATE_BY_CODE: Record<string, FranchiseState> = Object.fromEntries(
  FRANCHISE_STATES.map((s) => [s.code, s]),
);

export const STATE_BY_NAME: Record<string, FranchiseState> = Object.fromEntries(
  FRANCHISE_STATES.map((s) => [s.name.toLowerCase(), s]),
);

export const STATUS_META: Record<
  FranchiseStatus,
  { label: string; tone: "primary" | "muted" | "accent" | "warning"; description: string }
> = {
  available: {
    label: "Available",
    tone: "primary",
    description: "Open — apply to claim this territory.",
  },
  in_talks: {
    label: "In talks",
    tone: "warning",
    description: "Warm lead in play. Get in front of them before it closes.",
  },
  taken: {
    label: "Awarded",
    tone: "accent",
    description: "Already awarded to a franchisee.",
  },
  reserved: {
    label: "Reserved",
    tone: "muted",
    description: "Not for sale — home region or corporate market.",
  },
};

export function availableCount(): number {
  return FRANCHISE_STATES.filter((s) => s.status === "available" || s.status === "in_talks")
    .length;
}
